/**
 * Admin V3 — Financial / Partners — top-level page
 *
 * Mounted at: /admin-v3/partners (see AdminV3.js)
 *
 * GOVERNANCE MEANING: Partner directory (read-only).
 * Source: GET /admin/partners (admin_partner.py:210-246), returns enriched
 * partners with current_balance + credit_limit from billing_profiles join.
 *
 * SCOPE FOR PHASE 2.11:
 *   - GET /admin/partners (server-side status + type filters)
 *   - Client-side search over loaded page (name + contact_email)
 *   - Per-row expansion with address, pricing_profile_id, billing_profile_id,
 *     tenant_id. The api_key field is DELIBERATELY OMITTED from display
 *     (defense-in-depth even though backend returns it).
 *   - Row click → /admin-v3/partners/:id (placeholder)
 *
 * MUTATIONS (AV3 A-7): RBAC-gated Activate/Suspend lifecycle only
 *   (POST /admin/partners/{id}/suspend|activate). Create/edit/pricing/credit are
 *   deliberately NOT surfaced — blocked by billing/credential/query-param
 *   entanglement of the canonical endpoints (see A-7 report).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Building2 } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';
import PartnersListSearchBar from './PartnersListSearchBar';
import PartnersListTable from './PartnersListTable';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 100;

const PartnersListPage = () => {
  const locale = DEFAULT_LOCALE;

  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [search]);

  const queryParams = { limit: DEFAULT_LIMIT };
  if (status) queryParams.status = status;
  if (type)   queryParams.type   = type;

  const { data, loading, error, refetch } = useAdminQuery(
    '/admin/partners',
    queryParams
  );

  const allPartners = Array.isArray(data?.partners) ? data.partners : [];

  // Client-side search by name + contact_email (case-insensitive)
  const partners = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return allPartners;
    return allPartners.filter((p) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.contact_email || '').toLowerCase().includes(q)
    );
  }, [allPartners, debouncedSearch]);

  const isFilterActive = Boolean(debouncedSearch) || Boolean(status) || Boolean(type);
  const isAtBackendCap = allPartners.length === DEFAULT_LIMIT;
  const showSkeleton = loading && !data;

  return (
    <SectionCard
      title={t('partners.title', locale)}
      icon={Building2}
      iconClass="bg-violet-100 text-violet-600"
    >
      <div className="space-y-4">
        <PartnersListSearchBar
          status={status}
          onStatusChange={setStatus}
          type={type}
          onTypeChange={setType}
          search={search}
          onSearchChange={setSearch}
          resultsCount={partners.length}
          isFilterActive={isFilterActive}
          loading={loading}
          locale={locale}
        />

        <PartnersListTable
          partners={partners}
          loading={showSkeleton}
          error={error}
          isFilterActive={isFilterActive}
          onRetry={refetch}
          onRefresh={refetch}
          locale={locale}
        />

        {isAtBackendCap && !loading && (
          <p
            className="text-[12px] text-slate-400 italic"
            data-testid="adminv3-partners-cap-hint"
          >
            {t('partners.cap.hint', locale).replace('{n}', String(DEFAULT_LIMIT))}
          </p>
        )}
      </div>
    </SectionCard>
  );
};

export default PartnersListPage;
